/**
 * Client SLA Assignments Tab Component
 *
 * Displays and manages SLA tier assignments for clients with history tracking.
 *
 * @module pages/Settings/SLAConfiguration/ClientAssignmentsTab
 */

import React, { useState, useEffect } from 'react';
import { ClientSLAAssignment, ClientSLAAssignmentWithTier } from '@shared/types/sla';
import slaService from '@services/slaService';
import AssignmentForm from '@components/SLA/AssignmentForm';
import AssignmentHistory from '@components/SLA/AssignmentHistory';
import { DualScrollTable, usePagination, PaginationControls } from '../../../components/common/TableControls';
import { formatEasternDate } from '@/utils/dateFormatter';

interface ClientAssignmentsTabProps {
  /** Optional callback when assignment is created */
  onAssignmentCreated?: () => void;
}

/**
 * Client Assignments Tab - Manage SLA tier assignments for clients
 */
const ClientAssignmentsTab: React.FC<ClientAssignmentsTabProps> = ({ onAssignmentCreated }) => {
  const [assignments, setAssignments] = useState<ClientSLAAssignmentWithTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(assignments);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all current assignments (not expired)
      const allAssignments = await slaService.getAssignments();
      const currentAssignments = allAssignments.filter(
        (a: ClientSLAAssignment) => a.expiration_date === null
      );

      setAssignments(currentAssignments);
    } catch (err) {
      console.error('Failed to load assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentCreated = () => {
    loadAssignments();
    setShowAssignmentForm(false);
    setSelectedClientId(null);
    if (onAssignmentCreated) {
      onAssignmentCreated();
    }
  };

  const handleViewHistory = (clientId: string) => {
    setSelectedClientId(clientId);
    setShowHistory(true);
  };

  const handleChangeAssignment = (clientId: string) => {
    setSelectedClientId(clientId);
    setShowAssignmentForm(true);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-[var(--color-text-secondary)]'>Loading assignments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md bg-red-50 dark:bg-red-900/20 p-4'>
        <div className='flex'>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-red-800 dark:text-red-300'>Error Loading Assignments</h3>
            <div className='mt-2 text-sm text-red-700 dark:text-red-400'>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Client SLA Assignments</h3>
          <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
            Assign SLA tiers to clients. Assignment changes are tracked with full history.
          </p>
        </div>
        <button
          type='button'
          onClick={() => setShowAssignmentForm(true)}
          className='inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'
        >
          New Assignment
        </button>
      </div>

      {/* Assignments Table */}
      {assignments.length === 0 ? (
        <div className='text-center py-12 border border-dashed border-[var(--color-border)] rounded-lg'>
          <svg
            className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
            />
          </svg>
          <h3 className='mt-2 text-sm font-medium text-[var(--color-text-primary)]'>No assignments</h3>
          <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
            Get started by assigning an SLA tier to a client.
          </p>
          <div className='mt-6'>
            <button
              type='button'
              onClick={() => setShowAssignmentForm(true)}
              className='inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'
            >
              New Assignment
            </button>
          </div>
        </div>
      ) : (
        <div className='overflow-hidden shadow ring-1 ring-[var(--color-border)] rounded-lg'>
          <DualScrollTable>
          <table className='min-w-full divide-y divide-[var(--color-border)]'>
            <thead className='bg-[var(--color-bg-subtle)]'>
              <tr>
                <th className='py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--color-text-primary)]'>
                  Client
                </th>
                <th className='px-3 py-3.5 text-left text-sm font-semibold text-[var(--color-text-primary)]'>
                  SLA Tier
                </th>
                <th className='px-3 py-3.5 text-left text-sm font-semibold text-[var(--color-text-primary)]'>
                  Effective SLA
                </th>
                <th className='px-3 py-3.5 text-left text-sm font-semibold text-[var(--color-text-primary)]'>
                  Assigned Date
                </th>
                <th className='relative py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-[var(--color-text-primary)]'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-[var(--color-border)] bg-[var(--color-bg-card)]'>
              {pageItems.map((assignment) => (
                <tr key={assignment.id} className='hover:bg-[var(--color-bg-subtle)]'>
                  <td className='whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-[var(--color-text-primary)]'>
                    {assignment.client_id}
                  </td>
                  <td className='whitespace-nowrap px-3 py-4 text-sm text-[var(--color-text-secondary)]'>
                    {assignment.tier_name}
                  </td>
                  <td className='px-3 py-4 text-sm text-[var(--color-text-secondary)]'>
                    <div className='space-y-1'>
                      <div>Uptime: {assignment.effective_uptime_percent}%</div>
                      <div>RTO: {assignment.effective_rto_hours}h</div>
                      <div>RPO: {assignment.effective_rpo_hours}h</div>
                    </div>
                  </td>
                  <td className='whitespace-nowrap px-3 py-4 text-sm text-[var(--color-text-secondary)]'>
                    {formatEasternDate(assignment.effective_date)}
                  </td>
                  <td className='relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium space-x-2'>
                    <button
                      type='button'
                      onClick={() => handleViewHistory(assignment.client_id)}
                      className='text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]'
                    >
                      History
                    </button>
                    <button
                      type='button'
                      onClick={() => handleChangeAssignment(assignment.client_id)}
                      className='text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]'
                    >
                      Change Tier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </DualScrollTable>
          <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
        </div>
      )}

      {/* Assignment Form Modal */}
      {showAssignmentForm && (
        <AssignmentForm
          preselectedClientId={selectedClientId || undefined}
          onAssignmentCreated={handleAssignmentCreated}
          onClose={() => {
            setShowAssignmentForm(false);
            setSelectedClientId(null);
          }}
        />
      )}

      {/* History Modal */}
      {showHistory && selectedClientId && (
        <AssignmentHistory
          clientId={selectedClientId}
          onClose={() => {
            setShowHistory(false);
            setSelectedClientId(null);
          }}
        />
      )}
    </div>
  );
};

export default ClientAssignmentsTab;
